// Route tree (TanStack Router, code-based). Product jobs at the top level;
// hierarchy and technical objects (versions, distributions, tags) are
// contextual detail screens reached from the work they belong to.
import { AdminCompanies, AdminCompanyDetail } from "./features/admin/admin-companies";
import { AdminHome } from "./features/admin/admin-home";
import { AdminBilling } from "./features/admin/admin-billing";
import { AdminFinance, AdminSettings } from "./features/admin/admin-finance";
import { AdminLayout } from "./features/admin/admin-layout";
import { AdminAuthorizations, AdminPurchaseOrders } from "./features/admin/admin-reviews";
import { PurchaseOrdersPage } from "./features/commercial/purchase-orders-page";
import { QueryClient } from "@tanstack/react-query";
import { createRootRoute, createRoute, createRouter, Navigate, Outlet, useRouterState } from "@tanstack/react-router";
import { CreditCard } from "lucide-react";
import { lazy, Suspense, useEffect } from "react";
import { useMe, useMemberships } from "./api/hooks";
import { StepUpDialog } from "./components/step-up-dialog";
import { AnalyticsPage } from "./features/analytics/analytics-page";
import { AuditPage } from "./features/audit/audit-page";
import { InvitePage } from "./features/auth/invite";
import { LoginPage } from "./features/auth/login";
import { OnboardingPage } from "./features/auth/onboarding";
import { CampaignDetailPage } from "./features/campaigns/campaign-detail";
import { CampaignsPage } from "./features/campaigns/campaigns-page";
import { BrandDetailPage, ClientDetailPage, ClientsPage } from "./features/clients/clients-page";
import { ComingSoon } from "./features/common/coming-soon";
import { PageError, PageLoading, useRouteParams } from "./features/common/page";
import { CreateCreativePage } from "./features/create/create-creative";
import { CreativeDetailPage } from "./features/creatives/creative-detail";
import { CreativesPage } from "./features/creatives/creatives-page";
import { VersionDetailPage } from "./features/creatives/version-detail";
import { DistributionDetailPage } from "./features/distributions/distribution-detail";
import { FrontDeskPage } from "./features/front-desk/front-desk";
import { SettingsPage } from "./features/settings/settings-page";
import { AppShell } from "./shell/app-shell";
import { lastOrg, rememberOrg } from "./shell/theme";
import { Toaster } from "./ui/toaster";
import { TooltipProvider } from "./ui/tooltip";
import { EmptyState } from "./ui/layout";

// The Creative Studio is a large, authoring-only module: loaded on demand.
const LazyStudio = lazy(() => import("./features/studio/studio-page").then((m) => ({ default: m.StudioPage })));
function StudioRoute() {
  return (
    <Suspense fallback={<FullPageLoading />}>
      <LazyStudio />
    </Suspense>
  );
}

function Root() {
  return (
    <TooltipProvider delayDuration={300}>
      <Outlet />
      <StepUpDialog />
      <Toaster />
    </TooltipProvider>
  );
}

/** "/" — send the user where they belong: login, onboarding or their workspace. */
function Home() {
  const me = useMe();
  const memberships = useMemberships(Boolean(me.data));
  if (me.isLoading || (me.data && memberships.isLoading)) return <FullPageLoading />;
  if (me.error) return <FullPageError error={me.error} />;
  if (!me.data) return <Navigate to="/login" />;
  const list = memberships.data ?? [];
  if (list.length === 0) return <Navigate to="/onboarding" />;
  const preferred = list.find((m) => m.organizationId === lastOrg()) ?? list[0];
  return <Navigate to={`/o/${preferred?.organizationId ?? ""}`} />;
}

/** Authenticated, membership-checked workspace layout. */
function OrgLayout() {
  const { orgId } = useRouteParams("orgId");
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const me = useMe();
  const memberships = useMemberships(Boolean(me.data));
  useEffect(() => {
    if (memberships.data?.some((m) => m.organizationId === orgId)) rememberOrg(orgId);
  }, [memberships.data, orgId]);
  if (me.isLoading || (me.data && memberships.isLoading)) return <FullPageLoading />;
  if (!me.data) return <Navigate to="/login" search={{ next: pathname }} />;
  // Not a member: same answer as for something that does not exist (no disclosure).
  if (!memberships.data?.some((m) => m.organizationId === orgId)) {
    return (
      <main id="main" className="grid min-h-dvh place-items-center px-4">
        <EmptyState
          title="No encontramos este espacio de trabajo"
          description="Puede que no exista o que no tengas acceso."
        />
      </main>
    );
  }
  // The Creative Studio is a full-screen workspace: same membership guard, no app shell.
  if (/\/versions\/[^/]+\/studio\/?$/.test(pathname)) return <Outlet />;
  return (
    <AppShell orgId={orgId}>
      <Outlet />
    </AppShell>
  );
}

function FullPageLoading() {
  return (
    <main id="main" className="mx-auto max-w-3xl p-8">
      <PageLoading />
    </main>
  );
}
function FullPageError({ error }: { error: unknown }) {
  return (
    <main id="main" className="mx-auto max-w-3xl p-8">
      <PageError error={error} />
    </main>
  );
}

const rootRoute = createRootRoute({
  component: Root,
  notFoundComponent: () => (
    <main id="main" className="grid min-h-dvh place-items-center px-4">
      <EmptyState title="No encontramos esta página" />
    </main>
  ),
});
const route = (path: string, component: () => React.ReactNode, parent = rootRoute) =>
  createRoute({ getParentRoute: () => parent, path, component });

const orgRoute = createRoute({ getParentRoute: () => rootRoute, path: "/o/$orgId", component: OrgLayout });
const inOrg = (path: string, component: () => React.ReactNode) =>
  createRoute({ getParentRoute: () => orgRoute, path, component });

// Administrador Master (ADR-0047): operators of Media Interactiva only; its own layout.
const adminRoute = createRoute({ getParentRoute: () => rootRoute, path: "/admin", component: AdminLayout });
const inAdmin = (path: string, component: () => React.ReactNode) =>
  createRoute({ getParentRoute: () => adminRoute, path, component });

const routeTree = rootRoute.addChildren([
  route("/", Home),
  route("/login", LoginPage),
  route("/onboarding", OnboardingPage),
  route("/invite", InvitePage),
  adminRoute.addChildren([
    inAdmin("/", AdminHome),
    inAdmin("/companies", AdminCompanies),
    inAdmin("/companies/$orgId", AdminCompanyDetail),
    inAdmin("/purchase-orders", AdminPurchaseOrders),
    inAdmin("/authorizations", AdminAuthorizations),
    inAdmin("/finance", AdminFinance),
    inAdmin("/billing", AdminBilling),
    inAdmin("/settings", AdminSettings),
  ]),
  orgRoute.addChildren([
    inOrg("/", FrontDeskPage),
    inOrg("/create", CreateCreativePage),
    inOrg("/creatives", CreativesPage),
    inOrg("/creatives/$creativeId", CreativeDetailPage),
    inOrg("/versions/$versionId", VersionDetailPage),
    inOrg("/versions/$versionId/studio", StudioRoute),
    inOrg("/distributions/$distributionId", DistributionDetailPage),
    inOrg("/campaigns", CampaignsPage),
    inOrg("/campaigns/$campaignId", CampaignDetailPage),
    inOrg("/clients", ClientsPage),
    inOrg("/clients/$clientId", ClientDetailPage),
    inOrg("/brands/$brandId", BrandDetailPage),
    inOrg("/audit", AuditPage),
    inOrg("/settings", SettingsPage),
    inOrg("/analytics", AnalyticsPage),
    inOrg("/purchase-orders", PurchaseOrdersPage),
    inOrg("/billing", () => (
      <ComingSoon
        title="Facturación"
        icon={<CreditCard />}
        description="La facturación de InteractAds aún no está disponible. La compra de medios sigue ocurriendo en tu plataforma publicitaria."
      />
    )),
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: false, scrollRestoration: true });
}
